import { useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { Button, Modal } from '@react-apps/design-system';
import {
  useHardDeleteContentItem,
  usePublishContentItem,
} from '../hooks/use-content-items';
import { notifyDone, notifyFailed } from '../display';
import { ConfirmModal } from './ConfirmModal';
import { ContentNotice } from './ContentNotice';

/**
 * O rascunho é estado de trabalho, não de espera (decisão 13 do épico
 * content-admin): a prova importada nasce `DRAFT`, e quem está nela publica ou
 * descarta antes de sair. Sem isto, a pessoa saía da tela e o rascunho ficava
 * para sempre.
 *
 * Enquanto `active`, três coisas: o aviso fixo com "Publicar tudo" e
 * "Descartar tudo"; o `useBlocker`, que segura a navegação para fora e abre o
 * modal; e o `beforeunload`, que pede a confirmação do navegador ao fechar a
 * aba ou recarregar — se a tela cair mesmo assim, a prova continua acessível
 * em rascunho.
 *
 * **Passam sem modal** a ficha de uma questão da própria prova (o "Abrir
 * questão" do card: editar a questão é abrir a ficha, decisão 11) e a volta a
 * esta mesma prova. A ficha não tem bloqueio: volta-se a esta prova pelo
 * breadcrumb dela, e o guard volta a valer aqui. Ver primum-core-root/docs/plans/in-review/
 * content-admin/question-and-official-comment/4-importacao-deixa-rascunho-esquecido.md.
 *
 * "Publicar tudo" publica a prova, e o back publica junto as questões e os
 * comentários `DRAFT` que ela contém. "Descartar tudo" é o hard-delete da
 * prova, que leva junto as questões dela, e volta para a lista de provas.
 */
export function DraftExamExitGuard({
  examId,
  active,
  questionIds,
  isMockExam,
}: {
  examId: string;
  active: boolean;
  /** As da tela: a ficha de cada uma passa sem o modal. */
  questionIds: string[];
  /** Simulado só aponta: publicar e descartar não levam as questões. */
  isMockExam: boolean;
}) {
  const navigate = useNavigate();
  const publish = usePublishContentItem();
  const discard = useHardDeleteContentItem();
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  // Sair depois de descartar não pode cair no próprio bloqueio: a prova ainda
  // está `DRAFT` no cache quando o `navigate` dispara.
  const leavingRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!active || leavingRef.current) return false;
    if (nextLocation.pathname === currentLocation.pathname) return false;
    return !questionIds.some(
      (id) => nextLocation.pathname === `/content/items/${id}`,
    );
  });

  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Navegadores mais antigos só pedem a confirmação com `returnValue`.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active]);

  const publishAll = () =>
    publish.mutate(examId, {
      onSuccess: () => {
        notifyDone(
          isMockExam
            ? 'Simulado publicado — já visível para o aluno'
            : 'Prova e questões publicadas — já visíveis para o aluno',
        );
        if (blocker.state === 'blocked') blocker.proceed();
      },
      onError: (err) => notifyFailed(err, 'Falha ao publicar a prova'),
    });

  const discardAll = () =>
    discard.mutate(examId, {
      onSuccess: () => {
        leavingRef.current = true;
        setIsDiscardOpen(false);
        if (blocker.state === 'blocked') blocker.reset();
        notifyDone('Prova descartada');
        navigate('/content/exams');
      },
      onError: (err) => notifyFailed(err, 'Falha ao descartar a prova'),
    });

  if (!active) return null;

  const busy = publish.isPending || discard.isPending;

  return (
    <>
      <ContentNotice
        action={
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={busy} onClick={publishAll}>
              {publish.isPending ? 'Publicando…' : 'Publicar tudo'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setIsDiscardOpen(true)}
            >
              Descartar tudo
            </Button>
          </div>
        }
      >
        {isMockExam ? 'Este simulado está' : 'Esta prova está'} em rascunho.
        Antes de sair, publique tudo ou descarte tudo.
      </ContentNotice>

      <Modal
        open={blocker.state === 'blocked' && !isDiscardOpen}
        onClose={() => blocker.reset?.()}
        title="A prova ainda está em rascunho"
        description="Publique a prova e as questões, ou descarte tudo, antes de sair. Rascunho deixado para trás fica esquecido."
      >
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => blocker.reset?.()}
          >
            Continuar aqui
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => setIsDiscardOpen(true)}
          >
            Descartar tudo
          </Button>
          <Button type="button" disabled={busy} onClick={publishAll}>
            {publish.isPending ? 'Publicando…' : 'Publicar tudo'}
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        open={isDiscardOpen}
        onClose={() => setIsDiscardOpen(false)}
        onConfirm={discardAll}
        title="Descartar a prova?"
        description={
          isMockExam
            ? 'O simulado é apagado de vez. As questões para as quais ele aponta ficam nas provas donas.'
            : 'A prova e todas as questões dela são apagadas de vez, junto com os comentários. Não dá para desfazer.'
        }
        confirmLabel="Descartar tudo"
        loading={discard.isPending}
      />
    </>
  );
}
